import { Formik, type FormikProps } from 'formik';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { object, string } from 'yup';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { StyledFormField } from '@/components/uielements/form-fields/styles';
import { Form, Input } from '@/components/uielements/formik';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { ConnectionErrorState } from '@/components/connectionError';
import { Render } from '@/components/uielements/render';
import { LAYOUT } from '@/constants/styles';
import { VALIDATION_STRINGS } from '@/constants/validation';
import authActions from '@/redux/auth/actions';
import { authSliceSelector } from '@/redux/auth/reducer';
import { useClearConnectionErrorsOnMount } from '@/hooks/useClearConnectionErrorsOnMount';

// Validation schema for the barcode submission form.
const forgotPasswordSchema = object({
  // The email must be valid and present.
  email: string().email(VALIDATION_STRINGS.EMAIL).required(VALIDATION_STRINGS.REQUIRED),
});

interface FormValues {
  email: string;
}

/**
 * Form to send an email with a link to reset a user's password.
 *
 * @TODO during Stitches overhaul, inline the SubmitButton style override.
 */
export function ForgotPasswordForm() {
  const dispatch = useDispatch();
  const { loading, forgotPasswordConnectionError, forgotPasswordSent } =
    useSelector(authSliceSelector);

  const formikRef = useRef<FormikProps<FormValues>>(null);

  // Clear the field only once the reset email is actually away, so a failure leaves the
  // diner looking at their own address rather than a blank input under an error banner.
  useEffect(() => {
    if (forgotPasswordSent) {
      formikRef.current?.resetForm();
    }
  }, [forgotPasswordSent]);

  useClearConnectionErrorsOnMount('forgotPassword');

  return (
    <Formik
      innerRef={formikRef}
      validateOnBlur
      validateOnChange={false}
      onSubmit={({ email }) => {
        dispatch(authActions.forgotPassword(email));
      }}
      initialValues={{ email: '' }}
      validationSchema={forgotPasswordSchema}
    >
      {({ dirty, isValid }) => (
        <Form>
          <Render condition={Boolean(forgotPasswordConnectionError)}>
            <ConnectionErrorState
              variant="banner"
              data-testid="forgot-password-connection-error"
              retryTestId="forgot-password-connection-retry"
              isRetrying={loading}
              // Re-submits the live field rather than a snapshot of the last attempt, so a
              // diner who corrects a typo before pressing Try again gets the corrected
              // address re-sent (and re-validated). Mirrors sign-up's retry.
              onRetry={() => void formikRef.current?.submitForm()}
            />
          </Render>
          <StyledFormField>
            <Input id="email" name="email" type="email" label="Email" autoFocus />
          </StyledFormField>
          <SubmitButton
            disabled={!dirty || !isValid}
            $submitting={loading}
            type="submit"
            $buttonTheme="secondary"
          >
            <StringAccessor
              accessor={
                loading ? 'forgot_password.submit_cta_loading' : 'forgot_password.submit_cta'
              }
            />
          </SubmitButton>
        </Form>
      )}
    </Formik>
  );
}

const SubmitButton = styled(StyledPrimaryButton)`
  margin-left: ${LAYOUT.GUTTER}px;
  @media (max-width: 767px) {
    margin: 0 auto;
  }
`;
